// src/ui/LoopEditor.tsx
import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { createSmoothedCopy } from "@audiocontrol/sampler-library/browser";

// src/ui/utils.ts
import { cn } from "@audiocontrol/editor-core";

// src/ui/draw-waveform.ts
function drawWaveform({
  canvas,
  samples,
  windowSamples,
  startSample,
  direction,
  endPoint,
  loopPoint,
  candidates,
  selectedCandidateIndex,
  colors
}) {
  const ctx = canvas.getContext("2d");
  if (!ctx || samples.length === 0) return;
  const width = canvas.width;
  const canvasHeight = canvas.height;
  const midY = canvasHeight / 2;
  ctx.fillStyle = direction === "left" ? colors.bgLeft : colors.bgRight;
  ctx.fillRect(0, 0, width, canvasHeight);
  ctx.strokeStyle = colors.center;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, midY);
  ctx.lineTo(width, midY);
  ctx.stroke();
  const samplesPerPixel = windowSamples / width;
  let sampleStart;
  let sampleEnd;
  if (direction === "left") {
    sampleEnd = startSample;
    sampleStart = sampleEnd - windowSamples;
  } else {
    sampleStart = startSample;
    sampleEnd = sampleStart + windowSamples;
  }
  sampleStart = Math.max(0, sampleStart);
  sampleEnd = Math.min(samples.length, sampleEnd);
  if (candidates.length > 0) {
    candidates.forEach((candidate, index) => {
      const candidatePoint = direction === "left" ? candidate.loopEnd : candidate.loopStart;
      const isSelected = index === selectedCandidateIndex;
      let xPos = null;
      if (candidatePoint >= sampleStart && candidatePoint <= sampleEnd) {
        if (direction === "left") {
          xPos = (candidatePoint - (startSample - windowSamples)) / windowSamples * width;
        } else {
          xPos = (candidatePoint - startSample) / windowSamples * width;
        }
      }
      if (xPos !== null) {
        ctx.strokeStyle = isSelected ? colors.selectedCandidate : colors.candidate;
        ctx.lineWidth = isSelected ? 2 : 1;
        ctx.setLineDash([4, 2]);
        ctx.beginPath();
        ctx.moveTo(xPos, 0);
        ctx.lineTo(xPos, canvasHeight);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = isSelected ? colors.selectedCandidate : colors.candidate;
        ctx.font = "9px monospace";
        ctx.fillText(`${index + 1}`, xPos + 2, canvasHeight - 4);
      }
    });
  }
  ctx.beginPath();
  ctx.strokeStyle = colors.waveform;
  ctx.lineWidth = 1;
  let firstPoint = true;
  for (let x = 0; x < width; x++) {
    let sampleIndex;
    if (direction === "left") {
      sampleIndex = Math.floor(sampleEnd - windowSamples + x * samplesPerPixel);
    } else {
      sampleIndex = Math.floor(sampleStart + x * samplesPerPixel);
    }
    if (sampleIndex < 0 || sampleIndex >= samples.length) continue;
    const pixelSampleStart = sampleIndex;
    const pixelSampleEnd = Math.min(samples.length - 1, Math.floor(sampleIndex + samplesPerPixel));
    let minVal = samples[pixelSampleStart];
    let maxVal = samples[pixelSampleStart];
    for (let s = pixelSampleStart; s <= pixelSampleEnd; s++) {
      if (samples[s] < minVal) minVal = samples[s];
      if (samples[s] > maxVal) maxVal = samples[s];
    }
    const yMin = midY - maxVal / 32768 * (midY - 2);
    const yMax = midY - minVal / 32768 * (midY - 2);
    if (firstPoint) {
      ctx.moveTo(x, yMin);
      firstPoint = false;
    }
    ctx.lineTo(x, yMin);
    ctx.lineTo(x, yMax);
  }
  ctx.stroke();
  ctx.fillStyle = colors.marker;
  ctx.font = "10px monospace";
  const label = direction === "left" ? `End: ${endPoint}` : `Loop: ${loopPoint}`;
  const labelX = direction === "left" ? width - 60 : 4;
  ctx.fillText(label, labelX, 12);
}

// src/ui/LoopEditorToolbar.tsx
import { AcToggle } from "@audiocontrol/editor-core";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var MIN_ZOOM = 1;
var MAX_ZOOM = 128;
var PLAYBACK_MODE_OPTIONS = [
  { value: "no-loop", label: "No Loop", dataTestId: "playback-mode-no-loop" },
  { value: "loop", label: "Loop", dataTestId: "playback-mode-loop" },
  { value: "smoothed-loop", label: "Smoothed", dataTestId: "playback-mode-smoothed-loop" }
];
var MIDI_OPTIONS = [
  { value: "off", label: "Off", dataTestId: "midi-toggle-off" },
  { value: "on", label: "On", dataTestId: "midi-toggle-on" }
];
function LoopEditorToolbar({
  onAutoDetect,
  isSearching,
  audio,
  isPlaying,
  previewMode,
  onPreviewLoop,
  onPreviewSmoothed,
  onStopPreview,
  playbackMode,
  onPlaybackModeChange,
  discontinuity,
  crossfadeLength,
  onCrossfadeLengthChange,
  midiEnabled,
  onMidiEnabledChange,
  activeNoteCount,
  onSmoothLoop,
  isSmoothing,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset
}) {
  return /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar", children: [
    /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__row", children: [
      /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__title-block", children: [
        /* @__PURE__ */ jsx("h3", { className: "ac-loop-toolbar__title", children: "Loop Editor" }),
        /* @__PURE__ */ jsx(
          "span",
          {
            className: "ac-loop-toolbar__eyebrow",
            title: "This feature is still being designed and not all the wrinkles have been smoothed out yet.",
            children: "Experimental"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__actions", children: [
        onAutoDetect && /* @__PURE__ */ jsx(
          "button",
          {
            onClick: onAutoDetect,
            disabled: isSearching,
            className: "ac-toolbar-btn",
            title: "Auto-detect loop points",
            children: isSearching ? "Searching\u2026" : "Auto-Detect"
          }
        ),
        audio && !isPlaying && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: onPreviewLoop,
              className: "ac-toolbar-btn",
              title: "Preview loop (hear the splice point)",
              "data-testid": "preview-loop-btn",
              children: "\u25B6 Preview"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: onPreviewSmoothed,
              className: "ac-toolbar-btn",
              title: "Preview with crossfade smoothing applied",
              children: "\u25B6 Smoothed"
            }
          )
        ] }),
        audio && isPlaying && /* @__PURE__ */ jsxs(
          "button",
          {
            onClick: onStopPreview,
            className: "ac-toolbar-btn ac-toolbar-btn--primary",
            title: "Stop playback",
            children: [
              "\u25A0 Stop",
              previewMode === "smoothed" ? " (smoothed)" : ""
            ]
          }
        ),
        onSmoothLoop && !onPlaybackModeChange && /* @__PURE__ */ jsx(
          "button",
          {
            onClick: () => onSmoothLoop("equal-power"),
            disabled: isSmoothing || isPlaying,
            className: "ac-toolbar-btn",
            title: "Apply crossfade at loop splice point to eliminate clicks",
            children: isSmoothing ? "Smoothing\u2026" : "Apply Smoothing"
          }
        )
      ] })
    ] }),
    onPlaybackModeChange && /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__row ac-loop-toolbar__row--secondary", children: [
      /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__field", children: [
        /* @__PURE__ */ jsx("span", { className: "ac-field-label", children: "Mode" }),
        /* @__PURE__ */ jsx(
          AcToggle,
          {
            value: playbackMode ?? "loop",
            options: PLAYBACK_MODE_OPTIONS,
            onChange: onPlaybackModeChange,
            ariaLabel: "Playback mode",
            name: "loop-playback-mode"
          }
        )
      ] }),
      discontinuity && /* @__PURE__ */ jsxs(
        "span",
        {
          "data-testid": "discontinuity-indicator",
          className: cn(
            "ac-loop-toolbar__discontinuity",
            discontinuity.needsSmoothing ? "ac-loop-toolbar__discontinuity--warn" : "ac-loop-toolbar__discontinuity--ok"
          ),
          title: `Amplitude step at splice: ${(discontinuity.normalizedAmplitudeStep * 100).toFixed(1)}%`,
          children: [
            discontinuity.needsSmoothing ? "\u26A0" : "\u2713",
            " ",
            (discontinuity.normalizedAmplitudeStep * 100).toFixed(1),
            "%"
          ]
        }
      ),
      onCrossfadeLengthChange && crossfadeLength !== void 0 && /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__field ac-loop-toolbar__field--xfade", children: [
        /* @__PURE__ */ jsx("span", { className: "ac-field-label", children: "Xfade" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "range",
            min: 8,
            max: 2048,
            step: 8,
            value: crossfadeLength,
            onChange: (e) => onCrossfadeLengthChange(parseInt(e.target.value, 10)),
            className: "ac-loop-toolbar__xfade-range",
            "data-testid": "crossfade-length-slider",
            title: `Crossfade length: ${crossfadeLength} samples`
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "ac-loop-toolbar__xfade-value", "data-testid": "crossfade-length-value", children: crossfadeLength })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__row ac-loop-toolbar__row--secondary", children: [
      onMidiEnabledChange && /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__field", children: [
        /* @__PURE__ */ jsx("span", { className: "ac-field-label", children: "MIDI" }),
        /* @__PURE__ */ jsx(
          AcToggle,
          {
            value: midiEnabled ? "on" : "off",
            options: MIDI_OPTIONS,
            onChange: (next) => onMidiEnabledChange(next === "on"),
            ariaLabel: "MIDI keyboard playback",
            name: "loop-midi-enabled",
            className: "ac-loop-toolbar__midi-toggle"
          }
        ),
        midiEnabled && activeNoteCount != null && activeNoteCount > 0 && /* @__PURE__ */ jsxs("span", { className: "ac-loop-toolbar__voices", children: [
          activeNoteCount,
          " ",
          activeNoteCount === 1 ? "voice" : "voices"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "ac-loop-toolbar__zoom", children: [
        /* @__PURE__ */ jsx("span", { className: "ac-field-label", children: "Zoom" }),
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: onZoomOut,
            disabled: zoom <= MIN_ZOOM,
            className: "ac-icon-btn",
            title: "Zoom out",
            "aria-label": "Zoom out",
            children: "\u2212"
          }
        ),
        /* @__PURE__ */ jsxs(
          "button",
          {
            onClick: onZoomReset,
            className: "ac-toolbar-btn ac-loop-toolbar__zoom-reset",
            title: "Reset zoom",
            children: [
              zoom,
              "x"
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: onZoomIn,
            disabled: zoom >= MAX_ZOOM,
            className: "ac-icon-btn",
            title: "Zoom in",
            "aria-label": "Zoom in",
            children: "+"
          }
        )
      ] })
    ] })
  ] });
}

// src/ui/LoopEditorNudges.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function LoopEditorNudges({
  endPoint,
  loopPoint,
  startPoint,
  onEndPointChange,
  onLoopPointChange,
  onCommit,
  nudgeEndPoint,
  nudgeLoopPoint
}) {
  return /* @__PURE__ */ jsxs2("div", { className: "ac-loop-nudges", children: [
    /* @__PURE__ */ jsxs2("div", { className: "ac-loop-nudge-field", children: [
      /* @__PURE__ */ jsx2("label", { className: "ac-field-label", children: "End Point" }),
      /* @__PURE__ */ jsxs2("div", { className: "ac-loop-nudge-row", children: [
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeEndPoint(-100), className: "ac-icon-btn", title: "Move end point -100 samples", "aria-label": "Move end point -100 samples", children: "\xAB" }),
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeEndPoint(-1), className: "ac-icon-btn", title: "Move end point -1 sample", "aria-label": "Move end point -1 sample", children: "\u2039" }),
        /* @__PURE__ */ jsx2(
          "input",
          {
            type: "number",
            value: endPoint,
            onChange: (e) => {
              onEndPointChange?.(Math.max(loopPoint, parseInt(e.target.value) || 0));
            },
            onBlur: () => onCommit?.(),
            className: "ac-input",
            "data-testid": "end-point-input"
          }
        ),
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeEndPoint(1), className: "ac-icon-btn", title: "Move end point +1 sample", "aria-label": "Move end point +1 sample", children: "\u203A" }),
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeEndPoint(100), className: "ac-icon-btn", title: "Move end point +100 samples", "aria-label": "Move end point +100 samples", children: "\xBB" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "ac-loop-nudge-field", children: [
      /* @__PURE__ */ jsx2("label", { className: "ac-field-label", children: "Loop Point" }),
      /* @__PURE__ */ jsxs2("div", { className: "ac-loop-nudge-row", children: [
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeLoopPoint(-100), className: "ac-icon-btn", title: "Move loop point -100 samples", "aria-label": "Move loop point -100 samples", children: "\xAB" }),
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeLoopPoint(-1), className: "ac-icon-btn", title: "Move loop point -1 sample", "aria-label": "Move loop point -1 sample", children: "\u2039" }),
        /* @__PURE__ */ jsx2(
          "input",
          {
            type: "number",
            value: loopPoint,
            onChange: (e) => {
              onLoopPointChange?.(Math.min(endPoint, Math.max(startPoint, parseInt(e.target.value) || 0)));
            },
            onBlur: () => onCommit?.(),
            className: "ac-input",
            "data-testid": "loop-point-input"
          }
        ),
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeLoopPoint(1), className: "ac-icon-btn", title: "Move loop point +1 sample", "aria-label": "Move loop point +1 sample", children: "\u203A" }),
        /* @__PURE__ */ jsx2("button", { onClick: () => nudgeLoopPoint(100), className: "ac-icon-btn", title: "Move loop point +100 samples", "aria-label": "Move loop point +100 samples", children: "\xBB" })
      ] })
    ] })
  ] });
}

// src/ui/LoopEditorCandidates.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function LoopEditorCandidates({
  candidates,
  selectedCandidateIndex,
  onCandidateSelect,
  onApplyCandidate,
  onLoopPointChange,
  onEndPointChange,
  onCommit
}) {
  if (candidates.length === 0) return null;
  return /* @__PURE__ */ jsxs3("div", { className: "ac-loop-candidates", children: [
    /* @__PURE__ */ jsxs3("div", { className: "ac-loop-candidates__head", children: [
      /* @__PURE__ */ jsxs3("span", { children: [
        "Loop Candidates (",
        candidates.length,
        ")"
      ] }),
      /* @__PURE__ */ jsx3("span", { children: "Click to apply \xB7 Score: NCC / Spectral / Slope" })
    ] }),
    /* @__PURE__ */ jsx3("div", { className: "ac-loop-candidates__list", children: candidates.map((candidate, index) => /* @__PURE__ */ jsxs3(
      "button",
      {
        onClick: () => {
          onCandidateSelect?.(index);
          if (onApplyCandidate) {
            onApplyCandidate(candidate.loopStart, candidate.loopEnd);
          } else {
            onLoopPointChange?.(candidate.loopStart);
            onEndPointChange?.(candidate.loopEnd);
          }
          onCommit?.();
        },
        className: "ac-loop-candidate",
        "data-selected": index === selectedCandidateIndex ? "true" : "false",
        children: [
          /* @__PURE__ */ jsxs3("span", { className: "ac-loop-candidate__bounds", children: [
            /* @__PURE__ */ jsx3("span", { className: "ac-loop-candidate__id", children: index + 1 }),
            /* @__PURE__ */ jsxs3("span", { children: [
              candidate.loopStart,
              " \u2192 ",
              candidate.loopEnd
            ] }),
            /* @__PURE__ */ jsxs3("span", { className: "ac-loop-candidate__bounds-length", children: [
              "(",
              candidate.loopEnd - candidate.loopStart,
              " samples)"
            ] })
          ] }),
          /* @__PURE__ */ jsxs3("span", { className: "ac-loop-candidate__scores", children: [
            (candidate.nccScore * 100).toFixed(0),
            "% /",
            " ",
            (candidate.spectralScore * 100).toFixed(0),
            "% /",
            " ",
            (candidate.slopeScore * 100).toFixed(0),
            "%"
          ] })
        ]
      },
      `${candidate.loopStart}-${candidate.loopEnd}`
    )) })
  ] });
}

// src/ui/LoopEditor.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var WAVEFORM_COLOR = "rgba(59, 130, 246, 0.8)";
var BACKGROUND_COLOR_LEFT = "#1a1a2e";
var BACKGROUND_COLOR_RIGHT = "#1e1e32";
var CENTER_LINE_COLOR = "#333";
var MARKER_COLOR = "rgba(34, 197, 94, 0.8)";
var CANDIDATE_MARKER_COLOR = "rgba(249, 115, 22, 0.8)";
var SELECTED_CANDIDATE_COLOR = "rgba(34, 197, 94, 1.0)";
var MIN_ZOOM2 = 1;
var MAX_ZOOM2 = 128;
var ZOOM_STEP = 2;
var DEFAULT_WINDOW_SAMPLES = 4096;
function LoopEditor({
  samples,
  sampleRate,
  startPoint,
  loopPoint,
  endPoint,
  onLoopPointChange,
  onEndPointChange,
  onCommit,
  height = 120,
  isLoading = false,
  loadingProgress,
  className,
  candidates = [],
  selectedCandidateIndex,
  onCandidateSelect,
  onApplyCandidate,
  onAutoDetect,
  isSearching = false,
  searchProgress,
  onSmoothLoop,
  isSmoothing = false,
  audio,
  playbackMode,
  onPlaybackModeChange,
  discontinuity,
  crossfadeLength,
  onCrossfadeLengthChange,
  midiEnabled,
  onMidiEnabledChange,
  activeNoteCount
}) {
  const leftCanvasRef = useRef(null);
  const rightCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [canvasContainerWidth, setCanvasContainerWidth] = useState(600);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartValue, setDragStartValue] = useState(0);
  const [previewMode, setPreviewMode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => {
    if (!audio) return;
    audio.onStateChange((state) => {
      setIsPlaying(state.playing);
      if (!state.playing) {
        setPreviewMode(null);
      }
    });
    return () => {
      audio.onStateChange(null);
    };
  }, [audio]);
  useEffect(() => {
    if (isPlaying && audio?.setLoopRegion && sampleRate > 0) {
      audio.setLoopRegion(loopPoint / sampleRate, endPoint / sampleRate);
    }
  }, [isPlaying, audio, loopPoint, endPoint, sampleRate]);
  const windowSamples = Math.round(DEFAULT_WINDOW_SAMPLES / zoom);
  const paneWidth = useMemo(() => Math.floor(canvasContainerWidth / 2), [canvasContainerWidth]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const updateWidth = () => {
      const style = getComputedStyle(container);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingRight = parseFloat(style.paddingRight) || 0;
      const contentWidth = container.clientWidth - paddingLeft - paddingRight;
      setCanvasContainerWidth(contentWidth);
    };
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(container);
    updateWidth();
    return () => resizeObserver.disconnect();
  }, []);
  const pixelToSampleOffset = useCallback(
    (pixelOffset) => {
      if (paneWidth === 0) return 0;
      const samplesPerPixel = windowSamples / paneWidth;
      return Math.round(pixelOffset * samplesPerPixel);
    },
    [paneWidth, windowSamples]
  );
  const handlePreviewLoop = useCallback(() => {
    if (!samples || !audio) return;
    try {
      audio.stop();
      setPreviewMode("normal");
      const buffer = audio.createBuffer(samples, sampleRate);
      audio.play(buffer, {
        loop: true,
        loopStart: loopPoint / sampleRate,
        loopEnd: endPoint / sampleRate
      });
    } catch (err) {
      console.error("Failed to preview loop:", err);
    }
  }, [samples, audio, sampleRate, loopPoint, endPoint]);
  const handlePreviewSmoothed = useCallback(() => {
    if (!samples || !audio) return;
    try {
      audio.stop();
      setPreviewMode("smoothed");
      const smoothed = createSmoothedCopy(samples, loopPoint, endPoint, { mode: "equal-power", crossfadeLength: 64 });
      const buffer = audio.createBuffer(smoothed, sampleRate);
      audio.play(buffer, {
        loop: true,
        loopStart: loopPoint / sampleRate,
        loopEnd: endPoint / sampleRate
      });
    } catch (err) {
      console.error("Failed to preview smoothed loop:", err);
    }
  }, [samples, audio, sampleRate, loopPoint, endPoint]);
  const handleStopPreview = useCallback(() => {
    audio?.stop();
    setPreviewMode(null);
  }, [audio]);
  useEffect(() => {
    if (!samples || samples.length === 0) return;
    const colors = {
      waveform: WAVEFORM_COLOR,
      bgLeft: BACKGROUND_COLOR_LEFT,
      bgRight: BACKGROUND_COLOR_RIGHT,
      center: CENTER_LINE_COLOR,
      marker: MARKER_COLOR,
      candidate: CANDIDATE_MARKER_COLOR,
      selectedCandidate: SELECTED_CANDIDATE_COLOR
    };
    const leftCanvas = leftCanvasRef.current;
    const rightCanvas = rightCanvasRef.current;
    if (leftCanvas) {
      drawWaveform({
        canvas: leftCanvas,
        samples,
        windowSamples,
        startSample: endPoint,
        direction: "left",
        endPoint,
        loopPoint,
        candidates,
        selectedCandidateIndex,
        colors
      });
    }
    if (rightCanvas) {
      drawWaveform({
        canvas: rightCanvas,
        samples,
        windowSamples,
        startSample: loopPoint,
        direction: "right",
        endPoint,
        loopPoint,
        candidates,
        selectedCandidateIndex,
        colors
      });
    }
  }, [samples, endPoint, loopPoint, windowSamples, paneWidth, height, zoom, candidates, selectedCandidateIndex]);
  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(MAX_ZOOM2, z * ZOOM_STEP));
  }, []);
  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(MIN_ZOOM2, z / ZOOM_STEP));
  }, []);
  const handleZoomReset = useCallback(() => {
    setZoom(1);
  }, []);
  const handleMouseDown = useCallback(
    (pane, e) => {
      setIsDragging(pane);
      setDragStartX(e.clientX);
      setDragStartValue(pane === "loop" ? loopPoint : endPoint);
    },
    [loopPoint, endPoint]
  );
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragStartX;
      const deltaSamples = -pixelToSampleOffset(deltaX);
      const newValue = Math.max(startPoint, Math.min(samples?.length ?? 0, dragStartValue + deltaSamples));
      if (isDragging === "loop") {
        onLoopPointChange?.(Math.min(newValue, endPoint));
      } else {
        onEndPointChange?.(Math.max(newValue, loopPoint));
      }
    },
    [isDragging, dragStartX, dragStartValue, pixelToSampleOffset, startPoint, endPoint, loopPoint, samples, onLoopPointChange, onEndPointChange]
  );
  const handleMouseUp = useCallback(() => {
    if (isDragging) onCommit?.();
    setIsDragging(null);
  }, [isDragging, onCommit]);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  const nudgeLoopPoint = useCallback(
    (delta) => {
      const newValue = Math.max(startPoint, Math.min(endPoint, loopPoint + delta));
      onLoopPointChange?.(newValue);
      onCommit?.();
    },
    [loopPoint, startPoint, endPoint, onLoopPointChange, onCommit]
  );
  const nudgeEndPoint = useCallback(
    (delta) => {
      const newValue = Math.max(loopPoint, Math.min(samples?.length ?? 0, endPoint + delta));
      onEndPointChange?.(newValue);
      onCommit?.();
    },
    [endPoint, loopPoint, samples, onEndPointChange, onCommit]
  );
  const loopLength = endPoint - loopPoint;
  const loopDurationMs = sampleRate > 0 ? loopLength / sampleRate * 1e3 : 0;
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!containerRef.current?.contains(document.activeElement)) return;
      const nudgeAmount = e.shiftKey ? 100 : 1;
      switch (e.key) {
        case "ArrowLeft":
          if (e.ctrlKey || e.metaKey) {
            nudgeLoopPoint(-nudgeAmount);
          } else {
            nudgeEndPoint(-nudgeAmount);
          }
          e.preventDefault();
          break;
        case "ArrowRight":
          if (e.ctrlKey || e.metaKey) {
            nudgeLoopPoint(nudgeAmount);
          } else {
            nudgeEndPoint(nudgeAmount);
          }
          e.preventDefault();
          break;
        case "+":
        case "=":
          handleZoomIn();
          e.preventDefault();
          break;
        case "-":
          handleZoomOut();
          e.preventDefault();
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nudgeLoopPoint, nudgeEndPoint, handleZoomIn, handleZoomOut]);
  if (isLoading) {
    return /* @__PURE__ */ jsxs4("div", { className: cn("ac-card", className), children: [
      /* @__PURE__ */ jsx4("h3", { className: "ac-title-md", style: { marginBottom: "var(--ac-space-3)" }, children: "Loop Editor" }),
      /* @__PURE__ */ jsxs4("div", { className: "ac-loop-progress", children: [
        /* @__PURE__ */ jsxs4("div", { className: "ac-loop-progress__head", children: [
          /* @__PURE__ */ jsx4("span", { children: "Loading wave data\u2026" }),
          loadingProgress !== void 0 && /* @__PURE__ */ jsxs4("span", { children: [
            loadingProgress.toFixed(0),
            "%"
          ] })
        ] }),
        loadingProgress !== void 0 && /* @__PURE__ */ jsx4("div", { className: "ac-loop-progress__bar", children: /* @__PURE__ */ jsx4("div", { className: "ac-loop-progress__fill", style: { width: `${loadingProgress}%` } }) })
      ] })
    ] });
  }
  if (!samples || samples.length === 0) {
    return /* @__PURE__ */ jsx4("div", { className: cn("ac-card", className), children: /* @__PURE__ */ jsx4("div", { className: "ac-text-muted", style: { textAlign: "center", padding: "var(--ac-space-6) 0" }, children: "No wave data available. Export or import a sample first." }) });
  }
  return /* @__PURE__ */ jsxs4("div", { className: cn("ac-card", className), ref: containerRef, tabIndex: 0, "data-loop-editor": true, children: [
    /* @__PURE__ */ jsx4(
      LoopEditorToolbar,
      {
        onAutoDetect,
        isSearching,
        audio: Boolean(audio),
        isPlaying,
        previewMode,
        onPreviewLoop: handlePreviewLoop,
        onPreviewSmoothed: handlePreviewSmoothed,
        onStopPreview: handleStopPreview,
        playbackMode,
        onPlaybackModeChange,
        discontinuity,
        crossfadeLength,
        onCrossfadeLengthChange,
        midiEnabled,
        onMidiEnabledChange,
        activeNoteCount,
        onSmoothLoop,
        isSmoothing,
        zoom,
        onZoomIn: handleZoomIn,
        onZoomOut: handleZoomOut,
        onZoomReset: handleZoomReset
      }
    ),
    isSearching && searchProgress && /* @__PURE__ */ jsxs4("div", { className: "ac-loop-progress", children: [
      /* @__PURE__ */ jsxs4("div", { className: "ac-loop-progress__head", children: [
        /* @__PURE__ */ jsx4("span", { children: searchProgress.stage }),
        /* @__PURE__ */ jsxs4("span", { children: [
          searchProgress.percent.toFixed(0),
          "%"
        ] })
      ] }),
      /* @__PURE__ */ jsx4("div", { className: "ac-loop-progress__bar", children: /* @__PURE__ */ jsx4("div", { className: "ac-loop-progress__fill", style: { width: `${searchProgress.percent}%` } }) })
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "ac-loop-info-bar", children: [
      /* @__PURE__ */ jsxs4("span", { children: [
        "Loop length: ",
        loopLength,
        " samples (",
        loopDurationMs.toFixed(1),
        "ms)"
      ] }),
      /* @__PURE__ */ jsx4("span", { children: "Drag to adjust \xB7 Arrow keys nudge \xB7 +/- to zoom" })
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "ac-loop-pane-labels", children: [
      /* @__PURE__ */ jsx4("span", { children: "\u2190 Approaching End Point" }),
      /* @__PURE__ */ jsx4("span", { children: "Loop Point Continues \u2192" })
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "ac-loop-canvas-row", ref: canvasContainerRef, children: [
      /* @__PURE__ */ jsx4(
        "canvas",
        {
          ref: leftCanvasRef,
          width: paneWidth,
          height,
          style: { height },
          className: cn("ac-loop-canvas", isDragging === "end" && "ac-loop-canvas--dragging"),
          onMouseDown: (e) => handleMouseDown("end", e)
        }
      ),
      /* @__PURE__ */ jsx4(
        "canvas",
        {
          ref: rightCanvasRef,
          width: paneWidth,
          height,
          style: { height },
          className: cn("ac-loop-canvas", isDragging === "loop" && "ac-loop-canvas--dragging"),
          onMouseDown: (e) => handleMouseDown("loop", e)
        }
      )
    ] }),
    /* @__PURE__ */ jsx4(
      LoopEditorNudges,
      {
        endPoint,
        loopPoint,
        startPoint,
        onEndPointChange,
        onLoopPointChange,
        onCommit,
        nudgeEndPoint,
        nudgeLoopPoint
      }
    ),
    /* @__PURE__ */ jsx4(
      LoopEditorCandidates,
      {
        candidates,
        selectedCandidateIndex,
        onCandidateSelect,
        onApplyCandidate,
        onLoopPointChange,
        onEndPointChange,
        onCommit
      }
    )
  ] });
}

// src/ui/hooks/useLoopDetection.ts
import { useCallback as useCallback2, useRef as useRef2, useState as useState2 } from "react";
function useLoopDetection() {
  const [isSearching, setIsSearching] = useState2(false);
  const [progress, setProgress] = useState2({
    percent: 0,
    stage: ""
  });
  const [candidates, setCandidates] = useState2([]);
  const [error, setError] = useState2(null);
  const workerRef = useRef2(null);
  const terminateWorker = useCallback2(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);
  const searchLoopPoints = useCallback2(
    (samples, sampleRate, targetEndPoint, config) => {
      terminateWorker();
      setIsSearching(true);
      setProgress({ percent: 0, stage: "Starting..." });
      setCandidates([]);
      setError(null);
      const worker = new Worker(
        // INTENTIONAL RELATIVE PATH — do not convert to @/ alias.
        // new URL() takes a string literal, not an import specifier. tsup/esbuild
        // do not resolve @/ aliases inside string literals, so the @/ path would
        // pass through verbatim into the bundle and break consuming vite builds.
        new URL("../workers/loop-detection.worker.js", import.meta.url),
        { type: "module" }
      );
      workerRef.current = worker;
      worker.onmessage = (event) => {
        const response = event.data;
        switch (response.type) {
          case "progress":
            setProgress({
              percent: response.percent,
              stage: response.stage
            });
            break;
          case "complete":
            setCandidates(response.candidates);
            setIsSearching(false);
            terminateWorker();
            break;
          case "error":
            setError(response.message);
            setIsSearching(false);
            terminateWorker();
            break;
        }
      };
      worker.onerror = (event) => {
        setError(event.message || "Worker error");
        setIsSearching(false);
        terminateWorker();
      };
      const request = {
        type: "search",
        samples,
        sampleRate,
        targetEndPoint,
        config
      };
      worker.postMessage(request, [samples.buffer]);
    },
    [terminateWorker]
  );
  const cancelSearch = useCallback2(() => {
    if (isSearching) {
      terminateWorker();
      setIsSearching(false);
      setProgress({ percent: 0, stage: "" });
    }
  }, [isSearching, terminateWorker]);
  const clearResults = useCallback2(() => {
    setCandidates([]);
    setError(null);
    setProgress({ percent: 0, stage: "" });
  }, []);
  return {
    isSearching,
    progress,
    candidates,
    error,
    searchLoopPoints,
    cancelSearch,
    clearResults
  };
}

// src/ui/hooks/use-loop-editor.ts
import { useState as useState3, useCallback as useCallback3, useEffect as useEffect2, useMemo as useMemo2, useRef as useRef3 } from "react";
import { createBrowserAudioPlayback } from "@audiocontrol/editor-core";
import { useSamplePlayer, createKeyboardNoteInput, createWebMidiNoteInput } from "@audiocontrol/synth-core";
import { createSmoothedCopy as createSmoothedCopy2, analyzeDiscontinuity } from "@audiocontrol/sampler-library/browser";
function useLoopEditor(params) {
  const {
    samples,
    sampleRate,
    initialLoopStart,
    initialLoopEnd,
    rootKey = 60,
    noteInput = null
  } = params;
  const [loopPoint, setLoopPoint] = useState3(initialLoopStart ?? 0);
  const [endPoint, setEndPoint] = useState3(initialLoopEnd ?? (samples?.length ?? 0));
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState3(void 0);
  const [playbackMode, setPlaybackMode] = useState3("loop");
  const [crossfadeLength, setCrossfadeLength] = useState3(64);
  const [prevSamples, setPrevSamples] = useState3(null);
  if (samples !== prevSamples) {
    setPrevSamples(samples);
    setLoopPoint(initialLoopStart ?? 0);
    setEndPoint(initialLoopEnd ?? (samples?.length ?? 0));
    setSelectedCandidateIndex(void 0);
  }
  const resetForNewSamples = useCallback3((loopStart, loopEnd) => {
    setLoopPoint(loopStart ?? 0);
    setEndPoint(loopEnd ?? (samples?.length ?? 0));
    setSelectedCandidateIndex(void 0);
  }, [samples]);
  const smoothedSamples = useMemo2(() => {
    if (!samples || loopPoint >= endPoint) return null;
    const loopLength = endPoint - loopPoint;
    if (loopLength < crossfadeLength) return null;
    try {
      return createSmoothedCopy2(samples, loopPoint, endPoint, {
        mode: "equal-power",
        crossfadeLength
      });
    } catch {
      return null;
    }
  }, [samples, loopPoint, endPoint, crossfadeLength]);
  const discontinuity = useMemo2(() => {
    if (!samples || loopPoint >= endPoint) return null;
    try {
      return analyzeDiscontinuity(samples, loopPoint, endPoint);
    } catch {
      return null;
    }
  }, [samples, loopPoint, endPoint]);
  const playerSamples = playbackMode === "smoothed-loop" ? smoothedSamples ?? samples : samples;
  const loopEnabled = playbackMode !== "no-loop";
  const audio = useMemo2(() => createBrowserAudioPlayback(), []);
  const {
    isSearching,
    progress,
    candidates,
    error: loopDetectionError,
    searchLoopPoints,
    clearResults
  } = useLoopDetection();
  const handleAutoDetect = useCallback3(() => {
    if (!samples) return;
    clearResults();
    setSelectedCandidateIndex(void 0);
    searchLoopPoints(new Int16Array(samples), sampleRate, endPoint);
  }, [samples, sampleRate, endPoint, clearResults, searchLoopPoints]);
  const handleApplyCandidate = useCallback3((loopStart, loopEnd) => {
    setLoopPoint(loopStart);
    setEndPoint(loopEnd);
  }, []);
  const prevIsSearchingRef = useRef3(false);
  useEffect2(() => {
    if (prevIsSearchingRef.current && !isSearching && candidates.length > 0) {
      const best = candidates[0];
      setLoopPoint(best.loopStart);
      setEndPoint(best.loopEnd);
      setSelectedCandidateIndex(0);
    }
    prevIsSearchingRef.current = isSearching;
  }, [isSearching, candidates]);
  const [midiEnabled, setMidiEnabled] = useState3(true);
  const noteOnRef = useRef3(null);
  const noteOffRef = useRef3(null);
  const dispatchNoteOn = useCallback3((note, velocity) => {
    if (!midiEnabled) return;
    noteOnRef.current?.(note, velocity);
  }, [midiEnabled]);
  const dispatchNoteOff = useCallback3((note) => {
    if (!midiEnabled) return;
    noteOffRef.current?.(note);
  }, [midiEnabled]);
  const playerInputRef = useRef3(null);
  if (!playerInputRef.current) {
    playerInputRef.current = {
      onNoteOn(handler) {
        noteOnRef.current = handler;
      },
      onNoteOff(handler) {
        noteOffRef.current = handler;
      },
      dispose() {
        noteOnRef.current = null;
        noteOffRef.current = null;
      }
    };
  }
  const keyboardInputRef = useRef3(null);
  if (!keyboardInputRef.current) {
    const kbd = createKeyboardNoteInput(rootKey);
    kbd.onNoteOn(dispatchNoteOn);
    kbd.onNoteOff(dispatchNoteOff);
    keyboardInputRef.current = kbd;
  }
  useEffect2(() => {
    let disposed = false;
    let input = null;
    createWebMidiNoteInput().then((result) => {
      if (disposed) {
        result.dispose();
        return;
      }
      input = result;
      result.onNoteOn(dispatchNoteOn);
      result.onNoteOff(dispatchNoteOff);
    }).catch(() => {
    });
    return () => {
      disposed = true;
      input?.dispose();
    };
  }, [dispatchNoteOn, dispatchNoteOff]);
  useEffect2(() => {
    if (!noteInput) return;
    noteInput.onNoteOn(dispatchNoteOn);
    noteInput.onNoteOff(dispatchNoteOff);
    return () => {
      noteInput.onNoteOn(null);
      noteInput.onNoteOff(null);
    };
  }, [noteInput, dispatchNoteOn, dispatchNoteOff]);
  useEffect2(() => {
    return () => {
      keyboardInputRef.current?.dispose();
      keyboardInputRef.current = null;
      playerInputRef.current?.dispose();
    };
  }, []);
  const { activeNotes } = useSamplePlayer({
    samples: playerSamples,
    sampleRate,
    rootKey,
    loopEnabled,
    loopStartSample: loopPoint,
    loopEndSample: endPoint,
    noteInput: playerInputRef.current
  });
  const editorProps = useMemo2(() => ({
    samples,
    sampleRate,
    startPoint: 0,
    loopPoint,
    endPoint,
    onLoopPointChange: setLoopPoint,
    onEndPointChange: setEndPoint,
    candidates,
    selectedCandidateIndex,
    onCandidateSelect: setSelectedCandidateIndex,
    onApplyCandidate: handleApplyCandidate,
    onAutoDetect: handleAutoDetect,
    isSearching,
    searchProgress: progress,
    audio,
    playbackMode,
    onPlaybackModeChange: setPlaybackMode,
    discontinuity,
    crossfadeLength,
    onCrossfadeLengthChange: setCrossfadeLength,
    midiEnabled,
    onMidiEnabledChange: setMidiEnabled,
    activeNoteCount: activeNotes.size
  }), [
    samples,
    sampleRate,
    loopPoint,
    endPoint,
    setLoopPoint,
    setEndPoint,
    candidates,
    selectedCandidateIndex,
    setSelectedCandidateIndex,
    handleApplyCandidate,
    handleAutoDetect,
    isSearching,
    progress,
    audio,
    playbackMode,
    setPlaybackMode,
    discontinuity,
    crossfadeLength,
    setCrossfadeLength,
    midiEnabled,
    setMidiEnabled,
    activeNotes.size
  ]);
  return {
    loopPoint,
    endPoint,
    setLoopPoint,
    setEndPoint,
    playbackMode,
    setPlaybackMode,
    crossfadeLength,
    setCrossfadeLength,
    discontinuity,
    isSearching,
    searchProgress: progress,
    candidates,
    selectedCandidateIndex,
    setSelectedCandidateIndex,
    handleAutoDetect,
    handleApplyCandidate,
    loopDetectionError,
    clearResults,
    audio,
    editorProps,
    midiEnabled,
    setMidiEnabled,
    activeNotes,
    resetForNewSamples
  };
}

// src/ui/LoopEditorDialog.tsx
import { useCallback as useCallback4 } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function LoopEditorDialog({
  open,
  onOpenChange,
  samples,
  sampleRate,
  sampleName,
  loopStart: initialLoopStart,
  loopEnd: initialLoopEnd,
  onSave,
  rootKey = 60,
  noteInput
}) {
  const editor = useLoopEditor({
    samples,
    sampleRate,
    initialLoopStart,
    initialLoopEnd,
    rootKey,
    noteInput
  });
  const handleSave = useCallback4(() => {
    onSave?.(editor.loopPoint, editor.endPoint);
    onOpenChange(false);
  }, [editor.loopPoint, editor.endPoint, onSave, onOpenChange]);
  return /* @__PURE__ */ jsx5(Dialog.Root, { open, onOpenChange, children: /* @__PURE__ */ jsxs5(Dialog.Portal, { children: [
    /* @__PURE__ */ jsx5(Dialog.Overlay, { className: "fixed inset-0 bg-black/50 z-50" }),
    /* @__PURE__ */ jsxs5(Dialog.Content, { className: "fixed z-50 inset-4 bg-s330-panel border border-s330-accent rounded-lg shadow-xl overflow-hidden flex flex-col", children: [
      /* @__PURE__ */ jsxs5("div", { className: "flex items-center justify-between p-4 border-b border-s330-accent shrink-0", children: [
        /* @__PURE__ */ jsxs5("div", { children: [
          /* @__PURE__ */ jsx5(Dialog.Title, { className: "text-lg font-bold text-s330-text", children: "Loop Editor" }),
          /* @__PURE__ */ jsxs5(Dialog.Description, { className: "text-sm text-s330-muted", children: [
            sampleName,
            " \u2014 ",
            sampleRate,
            " Hz"
          ] })
        ] }),
        /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-2", children: [
          onSave && /* @__PURE__ */ jsx5(
            "button",
            {
              onClick: handleSave,
              className: "ac-btn ac-btn-sm ac-btn-primary",
              children: "Save Loop Points"
            }
          ),
          /* @__PURE__ */ jsx5(Dialog.Close, { className: "p-2 text-s330-muted hover:text-s330-text transition-colors", children: /* @__PURE__ */ jsx5("svg", { className: "w-5 h-5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ jsx5("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })
        ] })
      ] }),
      /* @__PURE__ */ jsx5("div", { className: "flex-1 overflow-auto p-4", children: /* @__PURE__ */ jsx5(LoopEditor, { ...editor.editorProps }) })
    ] })
  ] }) });
}
export {
  LoopEditor,
  LoopEditorDialog,
  useLoopDetection,
  useLoopEditor
};
//# sourceMappingURL=index.js.map